import { useId } from "react"
import { Icon, type IconProps } from "@sembark-travel/ui-base"

// Icons for the predefined amenities. Keys of the default export match the
// amenity `key` returned by `/amenities` for hotels and rooms.

// Mask/clip ids must be unique per rendered icon, and `useId` output contains
// characters (e.g. ":") that break `url(#...)` references.
function useSvgId() {
	return "amenity-icon-" + useId().replace(/[^a-zA-Z0-9_-]/g, "")
}

export function TwentyFourHourSecurityIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M20 13C20 18 16.5 20.5 12.34 21.95C12.1222 22.0238 11.8855 22.0202 11.67 21.94C7.5 20.5 4 18 4 13V5.99996C4 5.73474 4.10536 5.48039 4.29289 5.29285C4.48043 5.10532 4.73478 4.99996 5 4.99996C7 4.99996 9.5 3.79996 11.24 2.27996C11.4519 2.09896 11.7214 1.99951 12 1.99951C12.2786 1.99951 12.5481 2.09896 12.76 2.27996C14.51 3.80996 17 4.99996 19 4.99996C19.2652 4.99996 19.5196 5.10532 19.7071 5.29285C19.8946 5.48039 20 5.73474 20 5.99996V13Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6.37598 18.9102C6.80269 17.7624 7.5704 16.7726 8.576 16.0738C9.5816 15.375 10.777 15.0007 12.0015 15.001C13.2261 15.0013 14.4212 15.3763 15.4265 16.0757C16.4317 16.775 17.1989 17.7652 17.625 18.9132"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 15C14.2091 15 16 13.2091 16 11C16 8.79086 14.2091 7 12 7C9.79086 7 8 8.79086 8 11C8 13.2091 9.79086 15 12 15Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function TwentyFourHourFrontDeskIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="-1"
					width="24"
					height="25"
				>
					<path d="M0 -0.000209808H24V23.9998H0V-0.000209808Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M9.49886 10.5854C9.49886 11.9456 8.39617 13.0483 7.03595 13.0483C5.67569 13.0483 4.573 11.9456 4.573 10.5854C4.573 9.22514 5.67569 8.12246 7.03595 8.12246C8.39617 8.12246 9.49886 9.22514 9.49886 10.5854Z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M0 22.6466H24"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M2.54968 22.6464V17.6358C2.54968 15.8789 3.97393 14.4546 5.73081 14.4546H8.3409C10.0978 14.4546 11.522 15.8789 11.522 17.6358V22.6464"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M7.03589 14.4543V20.0464"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M8.47644 4.20913C9.39196 3.17548 10.5731 2.35095 11.9647 1.8523C16.3796 0.270267 21.2409 2.56672 22.823 6.98155C24.4049 11.3964 22.1085 16.2578 17.6937 17.8398"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M19.1172 14.2945L17.6936 17.8397L21.3114 18.9349"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M14.0566 9.52327C14.3396 9.14856 14.4771 8.77112 14.4771 8.36931C14.4771 7.94406 14.3588 7.59385 14.1257 7.32845C13.8424 7.00585 13.4424 6.84231 12.9367 6.84231C12.5204 6.84231 12.1576 6.98195 11.8562 7.25954C11.5614 7.5384 11.4001 7.88481 11.3768 8.28915L11.365 8.49441L12.3789 8.68759L12.4038 8.43198C12.4396 8.06387 12.6388 7.84412 12.9367 7.84412C13.1717 7.84412 13.2656 7.92484 13.3143 7.98371C13.3969 8.08576 13.4353 8.20824 13.4353 8.36931C13.4353 8.58727 13.386 8.75377 13.1051 9.11124L11.2765 11.4536V12.0285H14.5637V11.0267H12.8944L14.0566 9.52327Z"
						fill="currentColor"
					/>
					<path
						d="M16.1683 10.0339L16.7998 8.90708V10.0339H16.1683ZM17.8416 10.0339V6.89558H16.8451L14.7743 10.5057V11.0357H16.7998V12.0285H17.8416V11.0357H18.4213V10.0339H17.8416Z"
						fill="currentColor"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function AccessibleBathroomGrabRailsIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="1"
				y="1"
				width="22"
				height="22"
			>
				<path d="M1.5 1.5H22.5V22.5H1.5V1.5Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M20.2441 21.8847H3.75586C2.84978 21.8847 2.11523 21.1502 2.11523 20.2441V3.75583C2.11523 2.84971 2.84978 2.1152 3.75586 2.1152H20.2441C21.1502 2.1152 21.8848 2.84971 21.8848 3.75583V20.2441C21.8848 21.1502 21.1502 21.8847 20.2441 21.8847Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.49805 16.9629C9.49805 18.322 8.39624 19.4238 7.03711 19.4238C5.67797 19.4238 4.57617 18.322 4.57617 16.9629C4.57617 15.6037 5.67797 14.5019 7.03711 14.5019"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.03711 12.8613V16.1426C7.03711 16.5956 7.40436 16.9629 7.85742 16.9629H10.7285L11.1387 19.4238H11.959"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.03711 14.502H9.49805"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.26758 11.6309C8.26758 12.3104 7.71666 12.8613 7.03711 12.8613C6.35756 12.8613 5.80664 12.3104 5.80664 11.6309C5.80664 10.9513 6.35756 10.4004 7.03711 10.4004C7.71666 10.4004 8.26758 10.9513 8.26758 11.6309Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.2393 4.92189C18.9162 4.70365 18.5268 4.57617 18.1075 4.57617C16.9896 4.57617 16.0833 5.48245 16.0833 6.60042C16.0833 7.71838 16.9896 8.62462 18.1075 8.62462C18.5616 8.62462 18.9351 8.47516 19.2185 8.22275C19.2771 8.17049 19.3319 8.11385 19.3828 8.05323"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.70306 4.62928L10.4918 8.61694C10.5208 8.69856 10.6366 8.69778 10.6645 8.61575L11.8961 4.6367"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M11.8961 4.62928L13.1246 8.61694C13.1536 8.69856 13.2693 8.69778 13.2972 8.61575L14.0891 4.6367"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function AccessibleRoomsIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="1"
				y="1"
				width="22"
				height="22"
			>
				<path d="M1.5 1.5H22.5V22.5H1.5V1.5Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M10.5035 2.62248L2.15619 9.0162V12.1162L11.9999 4.57651L21.8437 12.1162V9.0162L13.4964 2.62248C12.6134 1.94618 11.3865 1.94618 10.5035 2.62248Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M3.79681 10.8596V19.4238H20.2031V10.8596"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.2031 19.4238H3.79681C2.89074 19.4238 2.15619 20.1583 2.15619 21.0644V21.8847H21.8437V21.0644C21.8437 20.1583 21.1091 19.4238 20.2031 19.4238Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M14.4608 14.4609C14.4608 15.8201 13.359 16.9219 11.9999 16.9219C10.6408 16.9219 9.53897 15.8201 9.53897 14.4609C9.53897 13.1018 10.6408 12 11.9999 12"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M11.9999 10.3594V13.6406C11.9999 14.0937 12.3672 14.4609 12.8202 14.4609H15.6913L16.1015 16.9219H16.9218"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M11.9999 12H14.4608"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.2304 9.12892C13.2304 9.80846 12.6795 10.3594 11.9999 10.3594C11.3204 10.3594 10.7694 9.80846 10.7694 9.12892C10.7694 8.44937 11.3204 7.89845 11.9999 7.89845C12.6795 7.89845 13.2304 8.44937 13.2304 9.12892Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function AirportShuttleIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M8 6V12"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15 6V12"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 12H21.6"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18 18H21C21 18 21.5 16.3 21.8 15.2C21.9 14.8 22 14.4 22 14C22 13.6 21.9 13.2 21.8 12.8L20.4 7.8C20.1 6.8 19.1 6 18 6H4C3.46957 6 2.96086 6.21071 2.58579 6.58579C2.21071 6.96086 2 7.46957 2 8V18H5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 20C8.10457 20 9 19.1046 9 18C9 16.8954 8.10457 16 7 16C5.89543 16 5 16.8954 5 18C5 19.1046 5.89543 20 7 20Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 18H14"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 20C17.1046 20 18 19.1046 18 18C18 16.8954 17.1046 16 16 16C14.8954 16 14 16.8954 14 18C14 19.1046 14.8954 20 16 20Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function AtmIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M15 16V17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16.9629 7.734C16.9047 7.52302 16.7789 7.33697 16.6048 7.20438C16.4306 7.07179 16.2178 6.99999 15.9989 7H8.00294C7.78408 6.99999 7.57125 7.07179 7.39712 7.20438C7.22299 7.33697 7.09716 7.52302 7.03894 7.734L4.07294 18.467C3.99084 18.7639 3.97844 19.0758 4.03671 19.3783C4.09498 19.6808 4.22235 19.9658 4.40887 20.2109C4.59539 20.4561 4.83603 20.6549 5.11201 20.7918C5.388 20.9286 5.69188 20.9999 5.99994 21H17.9999C18.3079 20.9999 18.6117 20.9286 18.8875 20.7918C19.1634 20.655 19.404 20.4563 19.5905 20.2112C19.777 19.9662 19.9044 19.6814 19.9628 19.379C20.0211 19.0766 20.0089 18.7649 19.9269 18.468L16.9629 7.734Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2.678 8.5C2.46503 8.3124 2.29442 8.08161 2.17752 7.82299C2.06062 7.56436 2.00011 7.28382 2 7V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H20C20.5304 3 21.0391 3.21071 21.4142 3.58579C21.7893 3.96086 22 4.46957 22 5V7C22 7.2835 21.9397 7.56375 21.8232 7.82219C21.7066 8.08062 21.5365 8.31132 21.324 8.499"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 21L11 7"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function BabysittingChildcareIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M10 16C10.5 16.3 11.2 16.5 12 16.5C12.8 16.5 13.5 16.3 14 16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15 12H15.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19.38 6.813C20.0811 7.8324 20.5645 8.9854 20.8 10.2C21.1381 10.3638 21.4233 10.6195 21.6229 10.9378C21.8224 11.2562 21.9282 11.6243 21.9282 12C21.9282 12.3757 21.8224 12.7438 21.6229 13.0622C21.4233 13.3805 21.1381 13.6362 20.8 13.8C20.3683 15.8135 19.2592 17.618 17.6577 18.9125C16.0562 20.207 14.0592 20.9132 12 20.9132C9.94076 20.9132 7.94379 20.207 6.34231 18.9125C4.74083 17.618 3.63171 15.8135 3.2 13.8C2.86186 13.6362 2.57668 13.3805 2.37714 13.0622C2.17761 12.7438 2.07178 12.3757 2.07178 12C2.07178 11.6243 2.17761 11.2562 2.37714 10.9378C2.57668 10.6195 2.86186 10.3638 3.2 10.2C3.61426 8.1705 4.71589 6.34602 6.31902 5.03437C7.92216 3.72271 9.92866 3.00418 12 3C14 3 15.5 4.1 15.5 5.5C15.5 6.9 14.6 8 13.5 8C12.7 8 12 7.6 12 7"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 12H9.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function BanquetFacilitiesIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="0"
				width="24"
				height="24"
			>
				<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M0.703125 17.995C0.959062 18.094 1.20675 18.2217 1.44136 18.3781C2.7187 19.2297 4.42964 19.2297 5.70698 18.3781C6.98433 17.5265 8.64839 17.5265 9.92573 18.3781C11.2031 19.2297 12.8671 19.2297 14.1445 18.3781C15.4218 17.5265 17.0859 17.5265 18.3632 18.3781C19.6406 19.2297 21.3515 19.2297 22.6289 18.3781C22.842 18.236 23.0658 18.1177 23.2969 18.023V9.18746H0.703125V17.995Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.4375 19.0168V21.8438"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M3.5625 21.8438V19.0168"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.4062 9.1875V7.78125C13.4062 5.45128 15.295 3.5625 17.625 3.5625C19.9549 3.5625 21.8438 5.45128 21.8438 7.78125V9.1875"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M17.625 2.15625V3.5625"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M2.15625 9.1875V7.78125C2.15625 5.45128 4.04503 3.5625 6.375 3.5625C8.70492 3.5625 10.5938 5.45128 10.5938 7.78125V9.1875"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.375 2.15625V3.5625"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M0.703125 21.8438H23.2969"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function BarIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 12L4.20703 4.207C4.10812 4.10812 4.04076 3.98213 4.01347 3.84497C3.98618 3.70781 4.00018 3.56563 4.0537 3.43642C4.10723 3.30722 4.19787 3.19679 4.31416 3.1191C4.43046 3.04142 4.56718 2.99997 4.70703 3H19.293C19.4329 2.99997 19.5696 3.04142 19.6859 3.1191C19.8022 3.19679 19.8928 3.30722 19.9464 3.43642C19.9999 3.56563 20.0139 3.70781 19.9866 3.84497C19.9593 3.98213 19.8919 4.10812 19.793 4.207L12 12Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 12V22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 22H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function BicycleRentalIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M18.5 21C20.433 21 22 19.433 22 17.5C22 15.567 20.433 14 18.5 14C16.567 14 15 15.567 15 17.5C15 19.433 16.567 21 18.5 21Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5.5 21C7.433 21 9 19.433 9 17.5C9 15.567 7.433 14 5.5 14C3.567 14 2 15.567 2 17.5C2 19.433 3.567 21 5.5 21Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M15 6C15.5523 6 16 5.55228 16 5C16 4.44772 15.5523 4 15 4C14.4477 4 14 4.44772 14 5C14 5.55228 14.4477 6 15 6Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 17.5V14L9 11L13 8L15 11H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function BrailleTactileSignageIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M15.6211 7.76366H16.8515"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M15.6211 10.5879H16.8515"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M15.6211 13.4121H16.8515"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M15.6211 16.2363H16.8515"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M18.4453 7.76366H19.6758"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M18.4453 10.5879H19.6758"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M18.4453 13.4121H19.6758"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M18.4453 16.2363H19.6758"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M12.7969 7.76366H14.0273"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M12.7969 10.5879H14.0273"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M12.7969 13.4121H14.0273"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M12.7969 16.2363H14.0273"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M9.97261 7.76366H11.2031"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M7.14841 9.17577H8.37888"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M7.14841 14.8242H8.37888"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M9.97261 12H11.2031"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<path
				d="M9.97261 16.2363H11.2031"
				stroke="currentColor"
				strokeWidth="1.23047"
				strokeMiterlimit="10"
			/>
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="1"
				y="1"
				width="22"
				height="22"
			>
				<path d="M1.5 1.5H22.5V22.5H1.5V1.5Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M21.8848 19.0605H2.11523V4.93945H21.8848V19.0605Z"
					stroke="currentColor"
					strokeWidth="1.23047"
					strokeMiterlimit="10"
				/>
			</g>
		</Icon>
	)
}

export function BusinessCentreIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 12H12.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 6V4C16 3.46957 15.7893 2.96086 15.4142 2.58579C15.0391 2.21071 14.5304 2 14 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4V6"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M22 13C19.0328 14.959 15.5555 16.0033 12 16.0033C8.44445 16.0033 4.96721 14.959 2 13"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 6H4C2.89543 6 2 6.89543 2 8V18C2 19.1046 2.89543 20 4 20H20C21.1046 20 22 19.1046 22 18V8C22 6.89543 21.1046 6 20 6Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function CarRentalIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M19 17H21C21.6 17 22 16.6 22 16V13C22 12.1 21.3 11.3 20.5 11.1C18.7 10.6 16 10 16 10C16 10 14.7 8.6 13.8 7.7C13.3 7.3 12.7 7 12 7H5C4.4 7 3.9 7.4 3.6 7.9L2.2 10.8C2.06758 11.1862 2 11.5917 2 12V16C2 16.6 2.4 17 3 17H5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 19C8.10457 19 9 18.1046 9 17C9 15.8954 8.10457 15 7 15C5.89543 15 5 15.8954 5 17C5 18.1046 5.89543 19 7 19Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 17H15"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17 19C18.1046 19 19 18.1046 19 17C19 15.8954 18.1046 15 17 15C15.8954 15 15 15.8954 15 17C15 18.1046 15.8954 19 17 19Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function CctvIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M16.75 12H20.382C20.5524 12.0001 20.7199 12.0437 20.8687 12.1267C21.0175 12.2098 21.1426 12.3294 21.2322 12.4744C21.3217 12.6194 21.3728 12.7848 21.3804 12.955C21.3881 13.1252 21.3522 13.2946 21.276 13.447L19.242 17.516C19.1649 17.6702 19.0492 17.8018 18.9062 17.8981C18.7632 17.9944 18.5977 18.0521 18.4259 18.0656C18.254 18.0791 18.0815 18.0479 17.9253 17.9751C17.769 17.9022 17.6342 17.7903 17.534 17.65L15.41 14.68"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17.106 9.05316C17.343 9.17181 17.5232 9.3797 17.607 9.63115C17.6909 9.8826 17.6714 10.157 17.553 10.3942L14.447 16.6052C14.3883 16.7226 14.3069 16.8274 14.2077 16.9135C14.1085 16.9995 13.9932 17.0652 13.8686 17.1067C13.744 17.1482 13.6124 17.1647 13.4814 17.1554C13.3504 17.146 13.2225 17.111 13.105 17.0522L3.61 12.3002C2.92033 11.9528 2.39635 11.3463 2.15272 10.6136C1.90909 9.88079 1.96564 9.08134 2.31 8.39016L3.69 5.60016C3.862 5.25738 4.09984 4.95184 4.38994 4.70099C4.68004 4.45015 5.01671 4.2589 5.38072 4.13818C5.74474 4.01746 6.12897 3.96963 6.51147 3.99742C6.89398 4.02522 7.26726 4.12809 7.61 4.30016L17.106 9.05316Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 19H5.76C6.13273 19.0026 6.49877 18.901 6.81682 18.7066C7.13487 18.5122 7.39228 18.2329 7.56 17.9L9 15"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 21V17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 9H7.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function ChildrensPlayAreaIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M12.375 6.54155V16.125H1.875V6.58941"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.5 6.375H0.703125L7.125 0.703125L13.5 6.375Z"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.5 16.2477V18.75H0.703125V16.125H1.79423"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.5 18.75H1.875V23.2969H4.5V18.75Z"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12.375 18.75H9.75V23.2969H12.375V18.75Z"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.125 13.5C8.36764 13.5 9.375 12.4926 9.375 11.25C9.375 10.0074 8.36764 9 7.125 9C5.88236 9 4.875 10.0074 4.875 11.25C4.875 12.4926 5.88236 13.5 7.125 13.5Z"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12.5774 16.125H13.4094C14.5899 16.125 15.6448 16.8622 16.0505 17.9708L16.6488 19.6052C17.4603 21.8224 19.57 23.2968 21.9311 23.2968H23.2969V20.25H22.2111C21.0327 20.25 19.9794 19.5154 19.572 18.4097L18.9809 16.8053C18.1662 14.5941 16.0594 13.125 13.7028 13.125H12.5535"
					stroke="currentColor"
					strokeWidth="1.40625"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function CoffeeShopIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M10 2V4"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14 2V4"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 8C16.2652 8 16.5196 8.10536 16.7071 8.29289C16.8946 8.48043 17 8.73478 17 9V17C17 18.0609 16.5786 19.0783 15.8284 19.8284C15.0783 20.5786 14.0609 21 13 21H7C5.93913 21 4.92172 20.5786 4.17157 19.8284C3.42143 19.0783 3 18.0609 3 17V9C3 8.73478 3.10536 8.48043 3.29289 8.29289C3.48043 8.10536 3.73478 8 4 8H18C19.0609 8 20.0783 8.42143 20.8284 9.17157C21.5786 9.92172 22 10.9391 22 12C22 13.0609 21.5786 14.0783 20.8284 14.8284C20.0783 15.5786 19.0609 16 18 16H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6 2V4"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function ConciergeIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M3 20C2.73478 20 2.48043 19.8946 2.29289 19.7071C2.10536 19.5196 2 19.2652 2 19V18C2 17.4696 2.21071 16.9609 2.58579 16.5858C2.96086 16.2107 3.46957 16 4 16H20C20.5304 16 21.0391 16.2107 21.4142 16.5858C21.7893 16.9609 22 17.4696 22 18V19C22 19.2652 21.8946 19.5196 21.7071 19.7071C21.5196 19.8946 21.2652 20 21 20H3Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 16C20 13.8783 19.1571 11.8434 17.6569 10.3431C16.1566 8.84285 14.1217 8 12 8C9.87827 8 7.84344 8.84285 6.34315 10.3431C4.84285 11.8434 4 13.8783 4 16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 4V8"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M10 4H14"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function ConferenceHallIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M2 3H22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M21 3V14C21 14.5304 20.7893 15.0391 20.4142 15.4142C20.0391 15.7893 19.5304 16 19 16H5C4.46957 16 3.96086 15.7893 3.58579 15.4142C3.21071 15.0391 3 14.5304 3 14V3"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 21L12 16L17 21"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function CurrencyExchangeIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="0"
					width="24"
					height="24"
				>
					<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M12.314 6.49231C12.314 9.69818 9.71518 12.297 6.50931 12.297C3.30348 12.297 0.70459 9.69818 0.70459 6.49231C0.70459 3.28643 3.30348 0.687591 6.50931 0.687591C9.71518 0.687591 12.314 3.28643 12.314 6.49231Z"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M23.2954 17.2436C23.2954 20.5953 20.5783 23.3124 17.2266 23.3124C13.8749 23.3124 11.1578 20.5953 11.1578 17.2436C11.1578 13.8919 13.8749 11.1748 17.2266 11.1748C20.5783 11.1748 23.2954 13.8919 23.2954 17.2436Z"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M8.37363 5.07806V4.97634C8.37363 4.06406 7.63408 3.32456 6.72185 3.32456C5.80957 3.32456 5.07007 4.06406 5.07007 4.97634V8.89453H8.98966"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M5.0701 8.89455H3.74133"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M3.74133 6.25598H6.87647"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M14.0485 2.03147H16.2985C18.2401 2.03147 19.8141 3.60548 19.8141 5.54709V8.70197"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M17.7047 6.75157L19.8324 8.702L21.7828 6.75157"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M2.24756 14.9163V17.1663C2.24756 19.1079 3.82157 20.6819 5.76318 20.6819H8.91806"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M6.96777 18.5726L8.9182 20.7003L6.96777 22.6507"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M19.623 14.4053H16.4374C15.6254 14.4053 14.9672 15.0636 14.9672 15.8756C14.9672 16.6876 15.6254 17.3458 16.4374 17.3458H18.0342C18.8305 17.3458 19.476 17.9914 19.476 18.7877C19.476 19.584 18.8305 20.2295 18.0342 20.2295H14.7222"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
					<path
						d="M17.1726 12.9714V21.5158"
						stroke="currentColor"
						strokeWidth="1.40625"
						strokeMiterlimit="10"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function DesignatedSmokingAreaIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M21 12H3.26667C2.93073 12 2.60854 12.1054 2.371 12.2929C2.13345 12.4804 2 12.7348 2 13V15C2 15.2652 2.13345 15.5196 2.371 15.7071C2.60854 15.8946 2.93073 16 3.26667 16H21"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18 8C18 5.5 16 5.5 16 3"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M21 16C21.2652 16 21.5196 15.8946 21.7071 15.7071C21.8946 15.5196 22 15.2652 22 15V13C22 12.7348 21.8946 12.4804 21.7071 12.2929C21.5196 12.1054 21.2652 12 21 12"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M22 8C22 5.5 20 5.5 20 3"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 12V16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function DryCleaningIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M20.38 3.46L16 2C16 3.06087 15.5786 4.07828 14.8285 4.82843C14.0783 5.57857 13.0609 6 12 6C10.9392 6 9.92176 5.57857 9.17161 4.82843C8.42147 4.07828 8.00004 3.06087 8.00004 2L3.62004 3.46C3.16742 3.61079 2.78362 3.91842 2.53789 4.32734C2.29217 4.73627 2.20072 5.21956 2.28004 5.69L2.86004 9.16C2.89812 9.39491 3.01872 9.60855 3.20018 9.76251C3.38164 9.91648 3.61206 10.0007 3.85004 10H6.00004V20C6.00004 21.1 6.90004 22 8.00004 22H16C16.5305 22 17.0392 21.7893 17.4143 21.4142C17.7893 21.0391 18 20.5304 18 20V10H20.15C20.388 10.0007 20.6184 9.91648 20.7999 9.76251C20.9814 9.60855 21.102 9.39491 21.14 9.16L21.72 5.69C21.7994 5.21956 21.7079 4.73627 21.4622 4.32734C21.2165 3.91842 20.8327 3.61079 20.38 3.46Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function ElevatorLiftIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="2"
				y="1"
				width="20"
				height="21"
			>
				<path d="M2 1.99979H22V21.9998H2V1.99979Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M21.0624 21.4136H2.93744C2.74326 21.4136 2.58588 21.2562 2.58588 21.062V2.93701C2.58588 2.74283 2.74326 2.58545 2.93744 2.58545H21.0624C21.2566 2.58545 21.414 2.74283 21.414 2.93701V21.062C21.414 21.2562 21.2566 21.4136 21.0624 21.4136Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.83335 6.19631L8.93491 4.9333L8.03644 6.19631"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M14.1665 4.93328L15.065 6.19629L15.9634 4.93328"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.13 21.4148H11.9999V8.54902H18.13V21.4148Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 21.4148H5.86987V8.54902H12V21.4148Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function EvChargingStationIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M14 13H16C16.5304 13 17.0391 13.2107 17.4142 13.5858C17.7893 13.9609 18 14.4696 18 15V17C18 17.5304 18.2107 18.0391 18.5858 18.4142C18.9609 18.7893 19.4696 19 20 19C20.5304 19 21.0391 18.7893 21.4142 18.4142C21.7893 18.0391 22 17.5304 22 17V10.002C22.0002 9.73809 21.9482 9.47674 21.8469 9.23303C21.7457 8.98932 21.5972 8.76806 21.41 8.582L18 5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14 21V5C14 4.46957 13.7893 3.96086 13.4142 3.58579C13.0391 3.21071 12.5304 3 12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V21"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 21H15"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M3 7H14"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 11L7 14H10L8 17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function FaxPhotocopyPrinterIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6 9V3C6 2.73478 6.10536 2.48043 6.29289 2.29289C6.48043 2.10536 6.73478 2 7 2H17C17.2652 2 17.5196 2.10536 17.7071 2.29289C17.8946 2.48043 18 2.73478 18 3V9"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17 14H7C6.44772 14 6 14.4477 6 15V21C6 21.5523 6.44772 22 7 22H17C17.5523 22 18 21.5523 18 21V15C18 14.4477 17.5523 14 17 14Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function FireExtinguishersIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M15 6.5V3C15 2.73478 14.8946 2.48043 14.7071 2.29289C14.5196 2.10536 14.2652 2 14 2H12C11.7348 2 11.4804 2.10536 11.2929 2.29289C11.1054 2.48043 11 2.73478 11 3V6.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 18H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18 3H15"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M11 3C9.4087 3 7.88258 3.63214 6.75736 4.75736C5.63214 5.88258 5 7.4087 5 9V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5 13H9"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17 10C17 8.93913 16.5786 7.92172 15.8284 7.17157C15.0783 6.42143 14.0609 6 13 6C11.9391 6 10.9217 6.42143 10.1716 7.17157C9.42143 7.92172 9 8.93913 9 10V20C9 20.5304 9.21071 21.0391 9.58579 21.4142C9.96086 21.7893 10.4696 22 11 22H15C15.5304 22 16.0391 21.7893 16.4142 21.4142C16.7893 21.0391 17 20.5304 17 20V10Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function FirstAidKitIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 11V15"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14 13H9.99998"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 6V4C16 3.46957 15.7893 2.96086 15.4142 2.58579C15.0392 2.21071 14.5305 2 14 2H10C9.46959 2 8.96088 2.21071 8.58581 2.58579C8.21074 2.96086 8.00002 3.46957 8.00002 4V6"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18 6V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6 6V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 6H4.00002C2.89545 6 2.00002 6.89543 2.00002 8V18C2.00002 19.1046 2.89545 20 4.00002 20H20C21.1046 20 22 19.1046 22 18V8C22 6.89543 21.1046 6 20 6Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function FitnessCentreGymIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M17.596 12.768C17.9712 13.1432 18.48 13.3539 19.0105 13.3539C19.5411 13.3539 20.0499 13.1432 20.425 12.768C20.8002 12.3929 21.0109 11.884 21.0109 11.3535C21.0109 10.823 20.8002 10.3142 20.425 9.93901L18.657 8.17201C19.0322 8.54702 19.5409 8.75765 20.0714 8.75756C20.334 8.75751 20.5941 8.70573 20.8367 8.60518C21.0794 8.50462 21.2998 8.35726 21.4855 8.17151C21.6712 7.98575 21.8185 7.76524 21.919 7.52257C22.0194 7.27989 22.0711 7.0198 22.0711 6.75715C22.071 6.4945 22.0192 6.23443 21.9187 5.99179C21.8181 5.74915 21.6708 5.5287 21.485 5.34301L18.657 2.51501C18.282 2.13986 17.7733 1.92905 17.2429 1.92896C16.7124 1.92886 16.2037 2.13949 15.8285 2.51451C15.4534 2.88952 15.2426 3.39821 15.2425 3.92865C15.2424 4.4591 15.453 4.96786 15.828 5.34301L14.061 3.57501C13.8753 3.38925 13.6547 3.2419 13.412 3.14137C13.1693 3.04084 12.9092 2.9891 12.6465 2.9891C12.3838 2.9891 12.1237 3.04084 11.881 3.14137C11.6383 3.2419 11.4178 3.38925 11.232 3.57501C11.0463 3.76076 10.8989 3.98128 10.7984 4.22398C10.6979 4.46669 10.6461 4.72681 10.6461 4.98951C10.6461 5.2522 10.6979 5.51233 10.7984 5.75503C10.8989 5.99773 11.0463 6.21825 11.232 6.40401L17.596 12.768Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2.5 21.5001L3.9 20.1001"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20.1 3.9L21.5 2.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5.34301 21.4849C5.71802 21.86 6.22671 22.0709 6.75715 22.071C7.0198 22.071 7.27989 22.0193 7.52257 21.9188C7.76524 21.8184 7.98575 21.6711 8.17151 21.4854C8.35726 21.2997 8.50462 21.0793 8.60518 20.8366C8.70573 20.594 8.75751 20.3339 8.75756 20.0713C8.7576 19.8086 8.70592 19.5485 8.60545 19.3058C8.50498 19.0632 8.3577 18.8427 8.17201 18.6569L9.93901 20.4249C10.3142 20.8 10.823 21.0108 11.3535 21.0108C11.884 21.0108 12.3929 20.8 12.768 20.4249C13.1432 20.0498 13.3539 19.5409 13.3539 19.0104C13.3539 18.4799 13.1432 17.971 12.768 17.5959L6.40401 11.2319C6.21825 11.0461 5.99773 10.8988 5.75503 10.7983C5.51233 10.6977 5.2522 10.646 4.98951 10.646C4.72681 10.646 4.46669 10.6977 4.22398 10.7983C3.98128 10.8988 3.76076 11.0461 3.57501 11.2319C3.38925 11.4177 3.2419 11.6382 3.14137 11.8809C3.04084 12.1236 2.9891 12.3837 2.9891 12.6464C2.9891 12.9091 3.04084 13.1692 3.14137 13.4119C3.2419 13.6546 3.38925 13.8751 3.57501 14.0609L5.34301 15.8279C4.96786 15.4529 4.4591 15.2423 3.92865 15.2423C3.39821 15.2424 2.88952 15.4533 2.51451 15.8284C2.13949 16.2035 1.92886 16.7123 1.92896 17.2428C1.92905 17.7732 2.13986 18.2819 2.51501 18.6569L5.34301 21.4849Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9.59998 14.4001L14.4 9.6001"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function FreeParkingIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9.29999 16.5V7.5H12.9C13.6161 7.5 14.3028 7.78446 14.8092 8.29081C15.3155 8.79716 15.6 9.48392 15.6 10.2C15.6 10.9161 15.3155 11.6028 14.8092 12.1092C14.3028 12.6155 13.6161 12.9 12.9 12.9H9.29999"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function GardenIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="1"
				y="2"
				width="21"
				height="21"
			>
				<path d="M1 2.00024H22V23H1V2.00024Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M17.1017 5.80882C17.3882 6.01626 17.5715 6.35618 17.5626 6.73654C17.56 6.84713 17.5414 6.95326 17.5091 7.05308C17.3652 7.50136 16.9451 7.82309 16.453 7.82458C16.4434 7.82458 16.4337 7.82458 16.4241 7.82421C15.9383 7.81307 15.5324 7.49282 15.3906 7.05531C15.3527 6.93916 15.3334 6.81484 15.3364 6.6857C15.3446 6.32538 15.5231 6.00921 15.7929 5.81142C15.984 5.67226 16.2204 5.5921 16.4749 5.59804C16.7095 5.60324 16.9251 5.68079 17.1017 5.80882Z"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.0072 7.53624C19.5645 7.13063 19.8377 6.40887 19.6284 5.71159C19.4117 4.98722 18.7459 4.52781 18.0271 4.52855C17.8145 3.8721 17.2118 3.38783 16.4826 3.37113C15.7249 3.35369 15.0807 3.84761 14.861 4.53486C14.1708 4.53412 13.5236 4.95753 13.2824 5.64591C13.0323 6.35951 13.3009 7.12321 13.8839 7.54514C13.672 8.20049 13.875 8.94452 14.4543 9.38612C15.0558 9.84441 15.8652 9.82474 16.4467 9.40022C16.4489 9.4017 16.4507 9.40319 16.453 9.40467C17.0107 9.80916 17.7811 9.8459 18.3797 9.43176C19.0031 8.99981 19.2339 8.22127 19.0072 7.53624Z"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.453 17.3122C16.453 14.6148 18.5118 12.2632 21.1287 11.6089V15.9251C21.1287 17.5312 20.2555 19.0144 19.1764 20.0898L19.176 20.0902"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.453 19.4018V17.3122"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.453 7.8242V17.3122"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.85963 8.14669C7.14611 8.35413 7.32943 8.69404 7.32052 9.07441C7.31792 9.185 7.29937 9.29113 7.26708 9.39095C7.1231 9.83923 6.70303 10.161 6.21096 10.1624C6.20131 10.1624 6.19167 10.1624 6.18202 10.1621C5.69626 10.1509 5.29029 9.83069 5.14853 9.39318C5.11068 9.27703 5.09138 9.15271 5.09435 9.02357C5.10252 8.66324 5.28101 8.34707 5.55079 8.14928C5.7419 8.01013 5.97829 7.92997 6.23286 7.93591C6.46738 7.9411 6.68299 8.01866 6.85963 8.14669Z"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.78506 6.86645C7.57243 6.20999 6.96978 5.72572 6.24059 5.70902C5.48282 5.69158 4.83861 6.1855 4.61893 6.87275C3.722 6.87164 2.87184 7.54702 2.87109 8.52893C2.87072 9.06218 3.20656 9.56798 3.64185 9.88303C3.42995 10.5384 3.63294 11.2824 4.21221 11.724C4.81375 12.1823 5.62309 12.1626 6.20459 11.7381C6.20682 11.7396 6.20867 11.7411 6.2109 11.7426C6.76865 12.147 7.53903 12.1838 8.1376 11.7697C8.76103 11.3377 8.99185 10.5592 8.76511 9.87413C9.19446 9.56167 9.50803 9.06107 9.50952 8.52893C9.51174 7.56112 8.68681 6.8657 7.78506 6.86645Z"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.21091 10.1621V18.4693"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.59339 20.098C4.216 20.4969 3.98444 21.0357 3.98444 21.6284"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.6887 19.8104C13.3495 19.2886 12.8326 18.8674 12.2333 18.6017C11.7806 18.4006 11.2807 18.2885 10.7753 18.2885C10.2261 18.2885 9.68612 18.421 9.20519 18.6563C8.58473 18.9594 8.06186 19.4326 7.74124 20.0111C7.34232 19.6333 6.8035 19.4018 6.21087 19.4018C5.57371 19.4018 4.99926 19.6693 4.59366 20.0979"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.7928 21.6284C19.7928 21.0317 19.5579 20.4895 19.1765 20.0898C18.7705 19.6661 18.1994 19.4018 17.5663 19.4018C17.1607 19.4018 16.7803 19.5102 16.453 19.6998C16.3102 19.7822 16.1769 19.8802 16.0567 19.9915C15.7506 19.6308 15.2934 19.4018 14.7831 19.4018C14.3649 19.4018 13.9819 19.5558 13.6888 19.8104C13.3362 20.1166 13.1132 20.5682 13.1132 21.0717C13.1132 21.2669 13.1466 21.4543 13.2082 21.6284"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.59369 20.098H4.59332C3.56652 19.528 2.87109 18.4329 2.87109 17.1753V16.062C4.71578 16.062 6.2109 17.5572 6.2109 19.4018V18.4693C6.2109 16.3667 7.5561 14.5005 9.55071 13.8355V17.1753C9.55071 17.7071 9.42639 18.2099 9.20522 18.6563"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12.2333 18.6018C12.0819 18.1539 11.9999 17.6741 11.9999 17.1753V14.9488C14.4591 14.9488 16.453 16.9426 16.453 19.4018V19.6998"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M2.99994 22H20.9999"
					stroke="currentColor"
					strokeWidth="1.13999"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function KeyCardAccessIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M8 6H3C2.44772 6 2 6.44772 2 7V17C2 17.5523 2.44772 18 3 18H8C8.55228 18 9 17.5523 9 17V7C9 6.44772 8.55228 6 8 6Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M13 8.31982C13.6392 9.44097 13.9754 10.7093 13.9754 11.9998C13.9754 13.2904 13.6392 14.5587 13 15.6798"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16.46 6.20996C17.459 7.97615 17.9841 9.97079 17.9841 12C17.9841 14.0291 17.459 16.0238 16.46 17.79"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19.91 4.1001C21.2876 6.50473 22.0132 9.22749 22.0149 11.9988C22.0167 14.77 21.2945 17.4937 19.92 19.9001"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function KidsClubIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M21.75 16.5V23.2969H15.75C15.75 21.2259 14.0709 19.5469 12 19.5469C9.92906 19.5469 8.25 21.2259 8.25 23.2969H2.25V16.5H21.75Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 11.25H4.5V16.5H12V11.25Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.5 11.25H12V16.5H19.5V11.25Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M15.75 6H8.25V11.25H15.75V6Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M17.625 6H6.375L12 0.703125L17.625 6Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function KidsMealsIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="0"
					width="24"
					height="24"
				>
					<path d="M0 -2.86102e-05H24V24H0V-2.86102e-05Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M10.0312 4.9687C10.0312 2.48344 8.10047 0.468704 5.71875 0.468704C3.33703 0.468704 1.40625 2.48344 1.40625 4.9687C1.40625 7.45397 3.33703 9.4687 5.71875 9.4687C8.10047 9.4687 10.0312 7.45397 10.0312 4.9687Z"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M6.96606 9.46871L7.58767 14.3905"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M8.11832 18.5923L8.36324 20.5316C8.56433 22.1237 7.32341 23.5312 5.71869 23.5312C4.11398 23.5312 2.87305 22.1237 3.07415 20.5316L4.47135 9.46869"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M4.20984 12H7.22695"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M19.1612 9.52572L20.5512 20.5316C20.7523 22.1237 19.5114 23.5312 17.9067 23.5312C16.302 23.5312 15.061 22.1237 15.2621 20.5316L16.6551 9.5027"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M16.3389 12H19.4736"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M22.5937 5.66558V1.42869C22.5937 0.920327 22.1985 0.487389 21.6904 0.469296C21.1571 0.450358 20.7187 0.877108 20.7187 1.40619V5.04819C20.7187 5.56597 20.299 5.98569 19.7812 5.98569C19.2635 5.98569 18.8437 5.56597 18.8437 5.04819V1.42869C18.8437 0.920327 18.4485 0.487389 17.9404 0.469296C17.4071 0.450358 16.9687 0.877108 16.9687 1.40619V5.04819C16.9687 5.56597 16.549 5.98569 16.0312 5.98569C15.5135 5.98569 15.0937 5.56597 15.0937 5.04819V1.42869C15.0937 0.920327 14.6985 0.487389 14.1904 0.469296C13.6571 0.450358 13.2188 0.877108 13.2188 1.40619V5.66558C13.2188 7.78475 14.9367 9.50272 17.0559 9.50272H18.7566C20.8758 9.50272 22.5937 7.78475 22.5937 5.66558Z"
						stroke="currentColor"
						strokeWidth="1.2"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function KidsPoolIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M2 12C3.66667 13.3333 5.33333 13.3333 7 12C8.66667 10.6667 10.3333 10.6667 12 12C13.6667 13.3333 15.3333 13.3333 17 12C18.6667 10.6667 20.3333 10.6667 22 12"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 19C3.66667 20.3333 5.33333 20.3333 7 19C8.66667 17.6667 10.3333 17.6667 12 19C13.6667 20.3333 15.3333 20.3333 17 19C18.6667 17.6667 20.3333 17.6667 22 19"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 5C3.66667 6.33333 5.33333 6.33333 7 5C8.66667 3.66667 10.3333 3.66667 12 5C13.6667 6.33333 15.3333 6.33333 17 5C18.6667 3.66667 20.3333 3.66667 22 5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function LaundryServiceIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M3 6H6"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17 6H17.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19 2H5C3.89543 2 3 2.89543 3 4V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V4C21 2.89543 20.1046 2 19 2Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 18C14.7614 18 17 15.7614 17 13C17 10.2386 14.7614 8 12 8C9.23858 8 7 10.2386 7 13C7 15.7614 9.23858 18 12 18Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 18C12.663 18 13.2989 17.7366 13.7678 17.2678C14.2366 16.7989 14.5 16.163 14.5 15.5C14.5 14.837 14.2366 14.2011 13.7678 13.7322C13.2989 13.2634 12.663 13 12 13C11.337 13 10.7011 12.7366 10.2322 12.2678C9.76339 11.7989 9.5 11.163 9.5 10.5C9.5 9.83696 9.76339 9.20107 10.2322 8.73223C10.7011 8.26339 11.337 8 12 8"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function LoungeIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="2"
				y="2"
				width="20"
				height="20"
			>
				<path d="M2 2H22V22H2V2Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M3.75775 10.8281V7.56539C3.75775 5.91461 4.93693 4.46879 6.55267 4.13059C7.4526 3.94226 8.51638 3.79687 9.65619 3.79687C10.796 3.79687 11.8598 3.94226 12.7597 4.13059C14.3754 4.46879 15.5156 5.91461 15.5156 7.56539V10.8281"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.6874 7.3125L19.0312 9.65625L21.414 7.3125H16.6874Z"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.0312 9.65622V20.2031"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.6874 12H21.414"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.6874 20.2031H21.414"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.31244 17.8594V20.2032H4.96869V17.8594"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M14.3437 17.8594V20.2032H11.9999V17.8594"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.1718 12V15.5156H6.14056V12C6.14056 11.3528 5.61588 10.8281 4.96869 10.8281H3.75775C3.11056 10.8281 2.58588 11.3528 2.58588 12V16.6875C2.58588 17.3347 3.11056 17.8593 3.75775 17.8593H15.5156C16.1628 17.8593 16.6874 17.3347 16.6874 16.6875V12C16.6874 11.3528 16.1628 10.8281 15.5156 10.8281H14.3437C13.6965 10.8281 13.1718 11.3528 13.1718 12Z"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function LuggageStorageIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 12V9C12 8.73478 11.8946 8.48043 11.7071 8.29289C11.5196 8.10536 11.2652 8 11 8H9C8.73478 8 8.48043 8.10536 8.29289 8.29289C8.10536 8.48043 8 8.73478 8 9V12"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 20V17C16 16.7348 15.8946 16.4804 15.7071 16.2929C15.5196 16.1054 15.2652 16 15 16H13C12.7348 16 12.4804 16.1054 12.2929 16.2929C12.1054 16.4804 12 16.7348 12 17V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 22V2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 12H20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 20H20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 2V22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 4H20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function MassageIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M14.9245 6.62553C15.354 4.73979 13.9234 2.95334 12 2.95334C10.0717 2.95334 8.64425 4.7486 9.07592 6.62599"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.6481 20.4241C16.6434 20.4288 16.5442 20.5609 16.3608 20.7494L15.1842 21.9551C13.4408 23.7424 10.5629 23.7452 8.81577 21.9551C7.54917 20.6572 7.53323 20.6508 7.35187 20.4251V20.4246"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.375 16.1252C9.789 16.5392 10.4606 16.5396 10.875 16.1252"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.125 16.1252C13.539 16.5392 14.2106 16.5396 14.625 16.1252"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M10.875 19.9096C11.5521 20.361 12.4395 20.3666 13.125 19.9096"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.93848 12.1687C5.40582 8.68564 8.38904 6.00021 12 6.00021C15.5821 6.00021 18.5707 8.65221 19.0563 12.1308"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.68671 13.4623C9.38896 10.0134 14.6137 10.017 17.3133 13.4623"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 6.00021L12.8371 6.83731C13.7226 7.72278 13.7187 9.15649 12.8371 10.0381L12 10.8752"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6 21.7502C5.99531 21.7502 6.1215 21.9049 1.875 16.5002L1.125 11.2502L0.703125 0.703354H5.25L4.875 12.3752L6.12572 13.3132C7.11464 14.0549 7.39242 15.4295 6.75 16.5002L6 16.1252L7.28208 19.9715C7.57378 20.8466 6.92241 21.7502 6 21.7502Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6 16.1252L5.25 15.3752"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18 21.7502C18.0047 21.7502 17.8785 21.9049 22.125 16.5002L22.875 11.2502L23.2969 0.703339H18.75L19.125 12.3752L17.8743 13.3132C16.8853 14.0549 16.6076 15.4295 17.25 16.5002L18 16.1252L16.7179 19.9714C16.4262 20.8466 17.0776 21.7502 18 21.7502Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18 16.1252L18.75 15.3752"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function NonSmokingPropertyIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 12H3C2.73478 12 2.48043 12.1054 2.29289 12.2929C2.10536 12.4804 2 12.7348 2 13V15C2 15.2652 2.10536 15.5196 2.29289 15.7071C2.48043 15.8946 2.73478 16 3 16H16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18 8C18 5.5 16 5.5 16 3"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 2L22 22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M21 12C21.2652 12 21.5196 12.1054 21.7071 12.2929C21.8946 12.4804 22 12.7348 22 13V15C22 15.1755 21.9538 15.348 21.866 15.5C21.7783 15.652 21.652 15.7782 21.5 15.866"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M22 8C22 5.5 20 5.5 20 3"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 12V16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function PaidParkingIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 17V7H13C13.7956 7 14.5587 7.31607 15.1213 7.87868C15.6839 8.44129 16 9.20435 16 10C16 10.7956 15.6839 11.5587 15.1213 12.1213C14.5587 12.6839 13.7956 13 13 13H9"
				stroke="currentColor"
				strokeWidth="1.56522"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function PetFriendlyIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M11.25 16.25H12.75L12 17L11.25 16.25Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M16 14V14.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4.42001 11.2471C4.14014 12.3278 3.999 13.4397 4.00001 14.5561C4.00001 18.7281 7.58201 21.0001 12 21.0001C16.418 21.0001 20 18.7281 20 14.5561C19.995 13.4352 19.829 12.3208 19.507 11.2471"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8 14V14.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8.49998 8.49997C8.11598 9.54997 7.41698 10.528 6.15598 11C4.22498 11.722 2.57998 10.703 2.49998 9.99997C2.38698 9.00597 3.67698 3.46997 6.49998 2.99997C8.42298 2.67897 10.151 3.84497 10.151 5.23497C11.4158 4.91375 12.7425 4.92822 14 5.27697C14 3.88697 15.844 2.67897 17.767 2.99997C20.59 3.46997 21.88 9.00597 21.767 9.99997C21.687 10.703 20.042 11.722 18.111 11C16.85 10.528 16.256 9.54997 15.872 8.49997"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function RestaurantIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M3 2V9C3 10.1 3.9 11 5 11H9C9.53043 11 10.0391 10.7893 10.4142 10.4142C10.7893 10.0391 11 9.53043 11 9V2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 2V22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M21 15V2C19.6739 2 18.4021 2.52678 17.4645 3.46447C16.5268 4.40215 16 5.67392 16 7V13C16 14.1 16.9 15 18 15H21ZM21 15V22"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function RoomServiceIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="0"
					width="24"
					height="25"
				>
					<path d="M0 0.000123978H24V24.0001H0V0.000123978Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M17.7952 22.6018C19.1582 21.2399 23.0915 17.411 23.0915 17.411C23.7229 16.8267 23.6663 15.8162 22.9735 15.3053C22.439 14.9112 21.6977 14.9523 21.2107 15.4031L17.2781 18.8821C17.2636 18.196 16.9078 17.6158 16.0374 17.3967L9.50663 16.1969C8.87841 16.0814 8.2343 16.2911 7.79649 16.7534L6.77278 17.8345H4.38464V21.0601H6.77278L7.63542 22.3627C7.983 22.8876 8.57288 23.2035 9.20513 23.2035H16.3253C16.8502 23.2035 17.4251 22.9716 17.7952 22.6018Z"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M0.46875 23.2034H4.38469V17.4108H0.46875V23.2034Z"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M10.6521 20.9151V19.9082C10.6521 19.3293 11.2088 18.9108 11.7694 19.0683L15.7056 20.1739C16.4771 20.265 17.1863 19.7174 17.278 18.9504V18.8839"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M13.0941 2.80694C12.9611 2.60294 12.8838 2.35933 12.8838 2.09763C12.8838 1.37941 13.466 0.797225 14.1841 0.797225C14.9023 0.797225 15.4845 1.37941 15.4845 2.09763C15.4845 2.36018 15.4066 2.60458 15.2728 2.80901"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M21.6143 11.1625H6.75415V10.1834C6.75415 6.07985 10.0807 2.75332 14.1842 2.75332C18.2877 2.75332 21.6143 6.07985 21.6143 10.1834V11.1625Z"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M22.5883 13.3047H5.78015V11.1625H22.5883V13.3047Z"
						stroke="currentColor"
						strokeWidth="1.3"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function SafetyDepositBoxReceptionIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M0.9375 7.3125H4.6875M4.6875 14.8125H0.9375M15.75 8.25V13.875M4.6875 23.25V20.4375M19.3125 23.25V20.4375M10.125 9.1875C9.08948 9.1875 8.25 10.027 8.25 11.0625C8.25 12.098 9.08948 12.9375 10.125 12.9375C11.1605 12.9375 12 12.098 12 11.0625C12 10.027 11.1605 9.1875 10.125 9.1875ZM4.6875 14.6875V7.4375C4.6875 6.33293 5.58293 5.4375 6.6875 5.4375H17.3125C18.4171 5.4375 19.3125 6.33293 19.3125 7.4375V14.6875C19.3125 15.7921 18.4171 16.6875 17.3125 16.6875H6.6875C5.58293 16.6875 4.6875 15.7921 4.6875 14.6875ZM21.0625 1.6875H2.9375C1.83293 1.6875 0.9375 2.58293 0.9375 3.6875V18.4375C0.9375 19.5421 1.83293 20.4375 2.9375 20.4375H21.0625C22.1671 20.4375 23.0625 19.5421 23.0625 18.4375V3.6875C23.0625 2.58293 22.1671 1.6875 21.0625 1.6875Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function SalonBeautyServicesIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M5.35699 14.6455V0.703125"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.04395 2.12512H6.66988"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M3.41235 4.75781H7.30148"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.04395 7.3905H6.66988"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.2879 23.2969H4.42603C3.64935 23.2969 3.01978 22.6673 3.01978 21.8906V14.7982H7.6941V21.8906C7.69415 22.6673 7.06453 23.2969 6.2879 23.2969Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.52 8.7897H17.4571V21.8907C17.4571 22.6673 16.8275 23.2969 16.0508 23.2969H14.9263C14.1496 23.2969 13.52 22.6673 13.52 21.8907V8.7897Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.944 2.50463C17.7037 0.102663 13.2735 0.102663 10.0331 2.50463C9.79754 4.64485 10.7244 6.74771 12.4632 8.01769L13.5201 8.78968H17.4571L18.514 8.01774C20.2527 6.74776 21.1796 4.64499 20.9441 2.50477L20.944 2.50463Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M15.4886 0.723633V4.05012"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.2348 1.23105V3.43244"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12.7423 1.23105V3.43244"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M17.4571 8.7897H13.52V11.6802H17.4571V8.7897Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function SmokeDetectorsIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M18.518 17.3472C17.2546 18.4146 15.654 19.0002 14 19.0002"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18.8 4C19.5894 5.54846 20.0006 7.26194 20 9"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 9H9.01"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 18C21.1046 18 22 17.1046 22 16C22 14.8954 21.1046 14 20 14C18.8954 14 18 14.8954 18 16C18 17.1046 18.8954 18 20 18Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 16C12.866 16 16 12.866 16 9C16 5.13401 12.866 2 9 2C5.13401 2 2 5.13401 2 9C2 12.866 5.13401 16 9 16Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 16H6C4.89543 16 4 16.8954 4 18V20C4 21.1046 4.89543 22 6 22H12C13.1046 22 14 21.1046 14 20V18C14 16.8954 13.1046 16 12 16Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function SpaAndWellnessCentreIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M4.63714 18.7108C4.56027 18.944 4.51953 19.1878 4.51953 19.4387C4.51953 21.1148 6.33866 22.4736 8.58266 22.4736C10.0166 22.4736 11.2768 21.9188 12 21.0809C12.7232 21.9187 13.9835 22.4736 15.4174 22.4736C17.6614 22.4736 19.4805 21.1148 19.4805 19.4387C19.4805 19.1878 19.4397 18.944 19.3629 18.7108"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.73053 9.93039C9.16942 6.38556 12 3.64398 12 3.64398C12 3.64398 14.8268 6.3819 15.2686 9.92326"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.6446 13.206C21.7872 13.2108 23.5312 13.9831 23.5312 13.9831C23.5312 13.9831 21.8951 17.6595 18.7109 18.9638C15.5266 20.2681 12 18.7064 12 18.7064"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.35239 13.2058C2.21119 13.2113 0.46875 13.9829 0.46875 13.9829C0.46875 13.9829 2.10487 17.6593 5.28914 18.9636C8.47341 20.2679 12 18.7062 12 18.7062"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.5244 15.0946C16.158 18.1668 12.0131 18.711 12.0131 18.711C12.0131 18.711 11.705 14.274 14.0715 11.2018C16.438 8.12953 20.5829 7.91353 20.5829 7.91353C20.5829 7.91353 20.8909 12.0224 18.5244 15.0946Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M5.4755 15.0945C7.84198 18.1667 11.9869 18.7108 11.9869 18.7108C11.9869 18.7108 12.2949 14.2739 9.92844 11.2016C7.56195 8.12941 3.41708 7.91341 3.41708 7.91341C3.41708 7.91341 3.10901 12.0222 5.4755 15.0945Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 1.5273V1.95813"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M22.2996 6.49626L21.9503 6.7485"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M17.6547 4.58197L17.4327 4.9512"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M1.70007 6.68123L2.04934 6.93351"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.34509 4.7668L6.56709 5.13603"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function SwimmingPoolIndoorIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="0"
				width="24"
				height="24"
			>
				<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M18.4442 12.1371C17.6204 12.9608 16.2848 12.9608 15.4611 12.1371C14.6373 11.3133 14.6373 9.97771 15.4611 9.15393C16.2848 8.33019 17.6204 8.33019 18.4442 9.15393C19.2679 9.97771 19.2679 11.3133 18.4442 12.1371Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M17.6487 15.731L10.8537 8.93591L14.2811 6.87969C14.9472 6.47961 15.1633 5.61584 14.7637 4.95017C14.368 4.28549 13.5003 4.06845 12.8342 4.46755L8.63335 6.98844C8.07258 7.32491 7.70602 7.89055 7.62638 8.53925C7.54627 9.18838 7.7662 9.82588 8.22844 10.2881L10.0773 12.137L6.41724 15.7971"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M0.703125 18.5675C2.25642 20.1208 4.8217 20.1208 6.375 18.5675C7.9283 20.1208 10.4467 20.1208 12 18.5675C13.5533 20.1208 16.0717 20.1208 17.625 18.5675C19.1783 20.1208 21.7436 20.1208 23.2969 18.5675"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M0.703125 15.755C2.25642 17.3083 4.8217 17.3083 6.375 15.755C7.9283 17.3083 10.4467 17.3083 12 15.755C13.5533 17.3083 16.0717 17.3083 17.625 15.755C19.1783 17.3083 21.7436 17.3083 23.2969 15.755"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function SwimmingPoolOutdoorIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M19 5C18.4696 5 17.9609 5.21071 17.5858 5.58579C17.2107 5.96086 17 6.46957 17 7V18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 18C2.6 18.5 3.2 19 4.5 19C7 19 7 17 9.5 17C12.1 17 11.9 19 14.5 19C17 19 17 17 19.5 17C20.8 17 21.4 17.5 22 18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 13H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7 9H17"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 5C8.46957 5 7.96086 5.21071 7.58579 5.58579C7.21071 5.96086 7 6.46957 7 7V18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function TerraceSunTerraceIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 15.6669C14.0251 15.6669 15.6667 14.0253 15.6667 12.0002C15.6667 9.97518 14.0251 8.33356 12 8.33356C9.97497 8.33356 8.33334 9.97518 8.33334 12.0002C8.33334 14.0253 9.97497 15.6669 12 15.6669Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 2.83356V4.66689"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 19.3336V21.1669"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5.51917 5.5191L6.81167 6.8116"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M17.1883 17.1885L18.4808 18.481"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2.83334 12H4.66668"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19.3333 12H21.1667"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6.81167 17.1885L5.51917 18.481"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M18.4808 5.5191L17.1883 6.8116"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function TourDeskIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="-1"
					width="24"
					height="25"
				>
					<path d="M0 -0.000211716H24V23.9998H0V-0.000211716Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M11.9999 0.702508C13.1431 0.702508 14.071 1.63035 14.071 2.77363C14.071 3.91687 13.1431 4.84471 11.9999 4.84471C10.8567 4.84471 9.92883 3.91687 9.92883 2.77363C9.92883 1.63035 10.8567 0.702508 11.9999 0.702508Z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M2.20935 23.2963H21.7906V11.2462H2.20935V23.2963Z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M15.0124 7.85718C15.0124 7.05811 14.695 6.29179 14.1301 5.727C13.5653 5.16215 12.799 4.84467 11.9999 4.84467C10.3363 4.84467 8.98743 6.19354 8.98743 7.85718V11.2462H15.0124V7.85718Z"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M8.61084 16.5182L9.36393 17.2713L8.61084 18.0244"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M9.36401 17.2713H16.1421"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M10.8702 14.2588L13.8828 17.2713L10.8702 20.2838"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M0.703125 11.2463H23.2969"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function ValetParkingIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M15.5155 7.31222H8.48438"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
			<path
				d="M10.2421 14.3433L11.9999 15.5152L13.7577 14.3433"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
			<path
				d="M13.7577 19.0698H16.1014"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="2"
				y="2"
				width="20"
				height="20"
			>
				<path d="M2.00018 2H21.9998V21.9996H2.00018V2Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M13.7577 14.3433L13.1718 12.9714C14.5373 12.4888 15.5155 11.1866 15.5155 9.6559C16.1627 9.6559 16.6873 9.13126 16.6873 8.48405C16.6873 7.83683 16.1627 7.3122 15.5155 7.3122V2.58574H8.48439V7.3122C7.83717 7.3122 7.31254 7.83683 7.31254 8.48405C7.31254 9.13126 7.83717 9.6559 8.48439 9.6559C8.48439 11.1866 9.46261 12.4888 10.8281 12.9714L10.2422 14.3433L6.14069 15.5151V21.4135H17.8592V15.5151L13.7577 14.3433Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
			</g>
		</Icon>
	)
}

export function WheelchairAccessibleIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="2"
				y="1"
				width="20"
				height="22"
			>
				<path d="M2 1H22V23H2V1Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M13.1719 16.7265C13.1719 19.3154 11.0732 21.414 8.48438 21.414C5.89555 21.414 3.79688 19.3154 3.79688 16.7265C3.79688 14.1377 5.89555 12.039 8.48438 12.039C11.0732 12.039 13.1719 14.1377 13.1719 16.7265Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.65625 16.7265C9.65625 17.3737 9.13156 17.8984 8.48438 17.8984C7.83719 17.8984 7.3125 17.3737 7.3125 16.7265C7.3125 16.0794 7.83719 15.5547 8.48438 15.5547C9.13156 15.5547 9.65625 16.0794 9.65625 16.7265Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4.96875 2.58594H6.14063C7.43504 2.58594 8.48438 3.63527 8.48438 4.92969V12.0391"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.2031 20.2422C20.2031 20.8894 19.6784 21.414 19.0313 21.414C18.3841 21.414 17.8594 20.8894 17.8594 20.2422C17.8594 19.595 18.3841 19.0703 19.0313 19.0703C19.6784 19.0703 20.2031 19.595 20.2031 20.2422Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.0242 15.5547H19.0312V19.0703"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.48438 8.48438H16.6875"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M11.5849 13.2109H17.8593C18.5066 13.2109 19.0312 13.7356 19.0312 14.3828V15.5547"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M15.5156 8.48436V13.2109"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
		</Icon>
	)
}

export function WifiIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M12 19.9999H12.0089"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M2 8.8199C4.75011 6.36013 8.31034 5.00024 12 5.00024C15.6897 5.00024 19.2499 6.36013 22 8.8199"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5 12.8591C6.86929 11.0268 9.38247 10.0005 12 10.0005C14.6175 10.0005 17.1307 11.0268 19 12.8591"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M8.5 16.4289C9.43464 15.5128 10.6912 14.9996 12 14.9996C13.3088 14.9996 14.5654 15.5128 15.5 16.4289"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function YogaClassesIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M10.6639 2.60789C10.5689 2.41177 10.5156 2.19164 10.5156 1.95905C10.5156 1.13597 11.1828 0.46875 12.0059 0.46875C12.829 0.46875 13.4962 1.13597 13.4962 1.95905C13.4962 2.19112 13.4432 2.41083 13.3486 2.60663"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M14.2887 7.77163C14.8524 7.19567 15.1999 6.40728 15.1999 5.5377C15.1999 3.7737 13.7698 2.34369 12.0058 2.34369C10.2418 2.34369 8.81183 3.7737 8.81183 5.5377C8.81183 6.40822 9.16006 7.19736 9.72481 7.7735"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.209 13.3646L15.5184 14.9435"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M11.9999 20.7689H15.6873C16.4501 20.7689 17.0685 21.3873 17.0685 22.1501C17.0685 22.9129 16.4501 23.5313 15.6873 23.5313H11.9999V20.7689Z"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.00502 23.5313H4.82789C3.73748 23.5313 2.85352 22.6473 2.85352 21.5569C2.85352 20.3348 3.95259 19.4062 5.15756 19.6102L12 20.7689V23.5313H10.2238"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.8423 19.6102L11.9999 20.7689V23.5313H19.172C20.2624 23.5313 21.1464 22.6473 21.1464 21.5569C21.1464 20.3348 20.0473 19.4062 18.8423 19.6102Z"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M6.73651 19.8776L6.75981 19.8714C7.92971 19.56 8.82596 18.6186 9.07946 17.4348H14.9205C15.174 18.6186 16.0703 19.56 17.2402 19.8714L17.2635 19.8776"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<circle cx="8.11438" cy="23.5312" r="0.46875" fill="currentColor" />
				<path
					d="M13.4191 9.05954L14.5127 9.32401C15.9405 9.66929 17.1387 10.6356 17.7786 11.9578L19.1306 14.7514C19.5502 15.6182 19.359 16.657 18.6583 17.3177C18.0264 17.9135 17.1038 18.0831 16.3013 17.7511L13.2673 16.4956C12.5038 16.1797 12.0059 15.435 12.0059 14.6087C12.0059 13.7948 12.0059 12.9054 12.0059 12.6812C12.0059 12.4453 12.1969 12.2543 12.4377 12.2543H12.4565C13.2088 12.2543 13.8186 12.8641 13.8185 13.6164L13.8185 14.2512L16.9356 15.6359"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M15.8386 14.5943L15.5184 13.3537"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M5.80273 13.3646L8.49341 14.9435"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M10.6037 9.05685L9.49909 9.324C8.07137 9.66928 6.87315 10.6356 6.23326 11.9578L4.8812 14.7514C4.46166 15.6182 4.65287 16.657 5.35356 17.3177C5.98543 17.9135 6.90807 18.0831 7.71052 17.751L10.7446 16.4956C11.508 16.1797 12.0059 15.4349 12.0059 14.6087C12.0059 13.7947 12.0059 12.9054 12.0059 12.6812C12.0059 12.4453 11.8149 12.2542 11.5741 12.2542H11.5554C10.8031 12.2542 10.1932 12.8641 10.1933 13.6164L10.1934 14.2512L7.07626 15.6359"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.1731 14.5943L8.49339 13.3537"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M9.65686 7.14794V5.5345C10.659 5.5345 11.5854 5.21144 12.3384 4.66412C13.1265 4.92034 13.8201 5.38539 14.355 5.99345V7.14789C14.355 8.4452 13.3033 9.49694 12.0059 9.49694C10.7085 9.49698 9.65686 8.44525 9.65686 7.14794Z"
					stroke="currentColor"
					strokeWidth="1.2"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function AirConditioningIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M21.8906 2.15625H2.10938C1.3327 2.15625 0.703125 2.78583 0.703125 3.5625V12C0.703125 12.7766 1.3327 13.4062 2.10938 13.4062H21.8906C22.6673 13.4062 23.2969 12.7766 23.2969 12V3.5625C23.2969 2.78583 22.6673 2.15625 21.8906 2.15625Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.4844 21.8438C18.7607 20.9819 17.6719 19.2202 17.6719 17.293V16.2188"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.4531 16.2188V18.4488C13.4531 19.7221 13.959 20.9434 14.8594 21.8438"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M10.5469 16.2188V18.4488C10.5469 19.7221 10.041 20.9434 9.14062 21.8438"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M3.51562 21.8438C5.23931 20.9819 6.32812 19.2202 6.32812 17.293V16.2188"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M3.51562 13.4062V12C3.51562 11.2233 4.1452 10.5938 4.92188 10.5938H16.2656C17.0423 10.5938 17.6719 11.2233 17.6719 12V13.4062"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<circle cx="20.4844" cy="10.5938" r="0.703125" fill="currentColor" />
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function BlackoutCurtainsIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-1)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="0"
					width="24"
					height="24"
				>
					<path d="M0 -2.86102e-05H24V24H0V-2.86102e-05Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<path
						d="M2.11047 0.703035V23.2968H7.30872C7.30872 16.078 5.43222 14.5801 5.43222 14.5801C5.43222 14.5801 9.65435 10.0468 9.65435 0.703035"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M5.43222 14.5801H2.11047"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M21.8889 0.703035V23.2968H16.6907C16.6907 16.078 18.5672 14.5801 18.5672 14.5801C18.5672 14.5801 14.345 10.0468 14.345 0.703035"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M18.5671 14.5801H21.8889"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M7.19989 20.482H16.7995"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M6.7865 17.6673H17.2128"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M23.9994 0.703033H0"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
					<path
						d="M9.52155 3.51778H14.4779"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeMiterlimit="10"
					/>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-1`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function DailyHousekeepingIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M11 2V4"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M12 3H10"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M13.5 10.5L22 2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14.7341 13.841C14.9561 13.4595 15.0457 13.0153 14.9889 12.5776C14.9321 12.1398 14.7321 11.7332 14.4201 11.421L12.5801 9.58C12.2678 9.26772 11.861 9.06756 11.423 9.01076C10.9851 8.95396 10.5406 9.04371 10.1591 9.266L2.50206 13.727C2.36961 13.8041 2.25667 13.9107 2.17196 14.0385C2.08725 14.1662 2.03304 14.3117 2.01352 14.4637C1.99399 14.6158 2.00968 14.7702 2.05936 14.9152C2.10905 15.0603 2.1914 15.1919 2.30006 15.3L8.70306 21.703C8.81131 21.8112 8.94294 21.8931 9.08782 21.9424C9.2327 21.9917 9.38696 22.0071 9.53873 21.9874C9.69049 21.9677 9.83571 21.9134 9.96319 21.8287C10.0907 21.744 10.197 21.6312 10.2741 21.499L14.7341 13.841Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 15V19"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M22 17H18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 4V8"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5 18L7 16"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6 6H2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7.69897 10.7L13.301 16.301"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function ElectricBlanketIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M10.3871 19.5901H3.29032M3.29032 19.5901C2.57772 19.5901 2 18.9852 2 18.2391C2 17.493 2.57772 16.8881 3.29032 16.8881H13.6129C15.0381 16.8881 16.1935 18.0978 16.1935 19.5901C16.1935 21.0825 15.0381 22.2922 13.6129 22.2922H3.29032C2.57772 22.2922 2 21.6873 2 20.9412C2 20.1951 2.57772 19.5901 3.29032 19.5901Z"
					stroke="currentColor"
					strokeWidth="1.37097"
					strokeMiterlimit="10"
				/>
				<path
					d="M2 18.2391L2.58387 6.01223C2.61819 5.29321 3.18499 4.72865 3.87256 4.72865H14.3154C15.0052 4.72865 15.573 5.29677 15.6043 6.01836L16.1935 19.5901"
					stroke="currentColor"
					strokeWidth="1.37097"
					strokeMiterlimit="10"
				/>
				<path
					d="M20.0645 5.40417C18.9956 5.40417 18.129 4.4969 18.129 3.37761V2.02657H22V3.37761C22 4.4969 21.1334 5.40417 20.0645 5.40417Z"
					stroke="currentColor"
					strokeWidth="0.8"
					strokeMiterlimit="10"
				/>
				<path
					d="M19.129 0V2.02656"
					stroke="currentColor"
					strokeWidth="0.6"
					strokeMiterlimit="10"
					strokeLinecap="round"
				/>
				<path
					d="M21 0V2.02656"
					stroke="currentColor"
					strokeWidth="0.6"
					strokeMiterlimit="10"
					strokeLinecap="round"
				/>
				<path
					d="M20.0645 5.40419V7.97117C20.0645 10.2842 18.2736 12.1594 16.0645 12.1594H15.871"
					stroke="currentColor"
					strokeMiterlimit="10"
				/>
				<path
					d="M7.48385 0C7.48385 1.01328 6.83868 1.01328 6.83868 2.02647C6.83868 3.03971 7.48385 3.03971 7.48385 4.05295C7.48385 5.06628 6.83868 5.06628 6.83868 6.0796C6.83868 7.09293 7.48385 7.09293 7.48385 8.10631"
					stroke="currentColor"
					strokeWidth="1.37097"
					strokeMiterlimit="10"
				/>
				<path
					d="M11.3549 0C11.3549 1.01328 10.7097 1.01328 10.7097 2.02647C10.7097 3.03971 11.3549 3.03971 11.3549 4.05295C11.3549 5.06628 10.7097 5.06628 10.7097 6.0796C10.7097 7.09293 11.3549 7.09293 11.3549 8.10631"
					stroke="currentColor"
					strokeWidth="1.37097"
					strokeMiterlimit="10"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function ElectricKettleIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="1"
				y="1"
				width="22"
				height="22"
			>
				<path d="M1 1H23V23H1V1Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M14.6531 19.45H3.43018C3.43018 20.5774 4.40757 21.4913 5.61326 21.4913H12.47C13.6757 21.4913 14.6531 20.5774 14.6531 19.45Z"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12.589 7.92592C13.3931 9.42893 14.9957 13.3059 14.253 19.3333H3.22844C2.48568 13.3059 4.08831 9.42893 4.89238 7.92592H12.589Z"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M5.48138 7.92593H12.8147C12.8147 6.12593 12.217 4.66667 11.4797 4.66667H6.8164C6.0791 4.66667 5.48138 6.12593 5.48138 7.92593Z"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.74072 17.7037C8.29072 17.7037 7.9259 17.4979 7.9259 17.2441V11.6448C7.9259 11.3909 8.29072 11.1852 8.74072 11.1852C9.19071 11.1852 9.55553 11.3909 9.55553 11.6448V17.2441C9.55553 17.4979 9.19071 17.7037 8.74072 17.7037Z"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.3696 9.44879C19.7652 9.44879 19.2753 8.95883 19.2753 8.35448V6.92796H21.4638V8.35448C21.4638 8.95883 20.9739 9.44879 20.3696 9.44879Z"
					stroke="currentColor"
					strokeWidth="0.855556"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M19.72 6.92794V5.48148"
					stroke="currentColor"
					strokeWidth="0.814815"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M21.0193 6.92794V5.48148"
					stroke="currentColor"
					strokeWidth="0.814815"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M20.3697 9.55555V17.1944C20.3697 17.996 19.7199 18.6458 18.9183 18.6458H14.8519"
					stroke="currentColor"
					strokeWidth="0.855556"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M5.16762 7.40886L2.22217 7.11111C2.22217 9.14026 2.8448 10.8294 3.53799 11.8903C3.95366 9.56315 4.75768 8.32335 5.16762 7.40886Z"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M10.8581 4.11993V3.35065C10.8581 2.88563 10.4811 2.50866 10.0161 2.50866H8.04079C7.57573 2.50866 7.19879 2.88563 7.19879 3.35065V4.11993"
					stroke="currentColor"
					strokeWidth="1.22222"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<mask id={`${id}-1`} fill="white">
					<path d="M15.7783 5.59114H12.9593C13.1002 5.90544 12.7865 6.30549 12.9593 6.78934C12.9571 6.78319 12.9614 6.77677 12.9593 6.77066H15.7783C16.0515 6.77066 16.2739 6.99994 16.2739 7.28181V10.8398C16.2739 12.1413 15.7027 13.3074 14.8058 14.0869C14.8047 14.0656 14.8034 14.0447 14.8022 14.0235C14.8275 14.4992 14.8431 14.9922 14.8469 15.5031C16.3862 14.5565 17.4174 12.8225 17.4174 10.8398V7.28181C17.4174 6.34807 16.6836 5.59114 15.7783 5.59114Z" />
				</mask>
				<path
					d="M12.9593 5.59114L11.844 6.09122C11.6745 5.71305 11.7081 5.27471 11.9333 4.92683C12.1586 4.57894 12.5448 4.36891 12.9593 4.36891V5.59114ZM12.9593 6.78934L14.1114 6.38143C14.3365 7.01716 14.0041 7.71505 13.3687 7.94095C12.7332 8.16685 12.0349 7.83537 11.8082 7.20023L12.9593 6.78934ZM12.9593 6.77066L11.8057 7.17442C11.6748 6.80056 11.7332 6.38648 11.9624 6.06343C12.1916 5.74038 12.5632 5.54844 12.9593 5.54844V6.77066ZM14.8058 14.0869L15.6075 15.0094C15.2545 15.3162 14.7573 15.3947 14.3269 15.2114C13.8966 15.0281 13.6085 14.6153 13.5851 14.1481L14.8058 14.0869ZM14.8022 14.0235L13.5817 14.0884C13.5459 13.4145 14.063 12.8391 14.7369 12.803C15.4107 12.767 15.9863 13.2838 16.0227 13.9577L14.8022 14.0235ZM14.8469 15.5031L15.4871 16.5442C15.1113 16.7752 14.6402 16.7859 14.2544 16.5721C13.8686 16.3582 13.6279 15.9531 13.6247 15.512L14.8469 15.5031ZM15.7783 5.59114V6.81336H12.9593V5.59114V4.36891H15.7783V5.59114ZM12.9593 5.59114L14.0745 5.09105C14.2231 5.42238 14.2289 5.73179 14.213 5.93693C14.1991 6.11726 14.1577 6.30584 14.1467 6.36203C14.111 6.54375 14.1447 6.47458 14.1103 6.37844L12.9593 6.78934L11.8082 7.20023C11.6011 6.62024 11.7053 6.10913 11.748 5.89129C11.7762 5.74769 11.776 5.74709 11.7758 5.74889C11.7738 5.7755 11.7659 5.91704 11.844 6.09122L12.9593 5.59114ZM12.9593 6.78934L11.8071 7.19724C11.7907 7.15099 11.7777 7.10545 11.7676 7.06113C11.7574 7.01688 11.7503 6.97458 11.7455 6.93481C11.7358 6.85562 11.7353 6.787 11.7375 6.73382C11.7386 6.70713 11.7404 6.68351 11.7424 6.6637C11.7443 6.64395 11.7465 6.62705 11.7483 6.61397C11.7501 6.60099 11.7518 6.59044 11.7529 6.58389C11.7534 6.58053 11.7539 6.57778 11.7542 6.576C11.7545 6.57433 11.7547 6.57292 11.7547 6.57281C11.755 6.57141 11.7542 6.57563 11.7534 6.58049C11.7525 6.58591 11.751 6.59536 11.7493 6.60721C11.7477 6.61916 11.7456 6.63496 11.7437 6.65362C11.7418 6.67233 11.74 6.69489 11.7389 6.72053C11.7366 6.77159 11.7368 6.83836 11.746 6.91588C11.7505 6.95484 11.7574 6.9964 11.7672 7.04001C11.777 7.08368 11.7896 7.12866 11.8057 7.17442L12.9593 6.77066L14.1129 6.3669C14.1292 6.41342 14.142 6.45916 14.152 6.5036C14.162 6.54798 14.1689 6.59031 14.1736 6.63004C14.1829 6.70911 14.1832 6.77743 14.1809 6.83004C14.1797 6.85647 14.1778 6.87981 14.1758 6.8993C14.1738 6.91874 14.1717 6.93532 14.1699 6.94806C14.1681 6.96069 14.1665 6.97092 14.1655 6.97712C14.165 6.98029 14.1645 6.98289 14.1642 6.98449C14.1641 6.9853 14.164 6.98598 14.1639 6.98641C14.1639 6.98662 14.1638 6.98681 14.1638 6.98693C14.1638 6.98703 14.1638 6.98715 14.1638 6.98716C14.1636 6.98812 14.1644 6.98373 14.1652 6.97843C14.1662 6.97266 14.1677 6.96289 14.1694 6.9507C14.1711 6.93841 14.1732 6.92229 14.1751 6.90331C14.1769 6.88429 14.1787 6.86146 14.1798 6.83554C14.182 6.78393 14.1814 6.71687 14.172 6.63922C14.1672 6.60023 14.1602 6.55871 14.1503 6.51522C14.1403 6.47168 14.1275 6.42691 14.1114 6.38143L12.9593 6.78934ZM12.9593 6.77066V5.54844H15.7783V6.77066V7.99288H12.9593V6.77066ZM15.7783 6.77066V5.54844C16.7618 5.54844 17.4961 6.36075 17.4961 7.28181H16.2739H15.0517C15.0517 7.63913 15.3412 7.99288 15.7783 7.99288V6.77066ZM16.2739 7.28181H17.4961V10.8398H16.2739H15.0517V7.28181H16.2739ZM16.2739 10.8398H17.4961C17.4961 12.5045 16.7644 14.0039 15.6075 15.0094L14.8058 14.0869L14.004 13.1644C14.6409 12.6108 15.0517 11.7781 15.0517 10.8398H16.2739ZM14.8058 14.0869L13.5851 14.1481C13.5838 14.1226 13.5842 14.1343 13.5818 14.0894L14.8022 14.0235L16.0227 13.9577C16.0231 13.9651 16.0235 13.9721 16.0242 13.9843C16.0248 13.9952 16.0257 14.0098 16.0265 14.0256L14.8058 14.0869ZM14.8022 14.0235L16.0227 13.9586C16.049 14.4524 16.0652 14.9641 16.0691 15.4941L14.8469 15.5031L13.6247 15.512C13.6211 15.0204 13.606 14.546 13.5817 14.0884L14.8022 14.0235ZM14.8469 15.5031L14.2066 14.4619C15.3892 13.7348 16.1952 12.3922 16.1952 10.8398H17.4174H18.6397C18.6397 13.2527 17.3832 15.3782 15.4871 16.5442L14.8469 15.5031ZM17.4174 10.8398H16.1952V7.28181H17.4174H18.6397V10.8398H17.4174ZM17.4174 7.28181H16.1952C16.1952 6.98714 15.9731 6.81336 15.7783 6.81336V5.59114V4.36891C17.394 4.36891 18.6397 5.70901 18.6397 7.28181H17.4174Z"
					fill="currentColor"
					mask={`url(#${id}-1)`}
				/>
			</g>
		</Icon>
	)
}

export function GeyserIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M12 7.52941C12.6498 7.52941 13.1765 7.00269 13.1765 6.35294C13.1765 5.70319 12.6498 5.17647 12 5.17647C11.3503 5.17647 10.8235 5.70319 10.8235 6.35294C10.8235 7.00269 11.3503 7.52941 12 7.52941Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M18.5882 0.470581H5.41176C4.632 0.470581 4 1.10258 4 1.88235V18.8235C4 19.6033 4.632 20.2353 5.41176 20.2353H18.5882C19.368 20.2353 20 19.6033 20 18.8235V1.88235C20 1.10258 19.368 0.470581 18.5882 0.470581Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M8.42355 22.1176H7.10591C6.69005 22.1176 6.35297 21.7806 6.35297 21.3647V20.2353H9.1765V21.3647C9.1765 21.7806 8.83941 22.1176 8.42355 22.1176Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M7.76471 22.1176V23.5294"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.8941 22.1176H15.5765C15.1606 22.1176 14.8235 21.7806 14.8235 21.3647V20.2353H17.6471V21.3647C17.6471 21.7806 17.31 22.1176 16.8941 22.1176Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M16.2353 22.1176V23.5294"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 16.9412C9.401 16.9412 7.29413 14.8343 7.29413 12.2353V3.29411H16.7059V12.2353C16.7059 14.8343 14.599 16.9412 12 16.9412Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 15.0588C13.5594 15.0588 14.8235 13.7947 14.8235 12.2353C14.8235 10.6759 13.5594 9.41176 12 9.41176C10.4406 9.41176 9.17645 10.6759 9.17645 12.2353C9.17645 13.7947 10.4406 15.0588 12 15.0588Z"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M4 3.29411H20"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M12 9.41176V11.2941"
					stroke="currentColor"
					strokeWidth="1.4"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function HairdryerIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M21.5525 3.7583H24"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M21.5525 8.90775H24"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M21.5525 6.33305H24"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M8.88675 11.5309V21.5164H4.55297L2.9519 10.8341"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M10.6825 14.2618H8.88672"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M10.6825 17.0743H8.88672"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M4.14148 18.7712H8.88673"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M6.33148 21.5164V23.9985"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M6.3315 9.0149C7.81268 9.0149 9.01341 7.81418 9.01341 6.333C9.01341 4.85182 7.81268 3.65109 6.3315 3.65109C4.85033 3.65109 3.6496 4.85182 3.6496 6.333C3.6496 7.81418 4.85033 9.0149 6.3315 9.0149Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M19.4431 9.22336V3.44269L7.28864 0.786096C6.97753 0.732799 6.6578 0.704674 6.3315 0.704674C3.22303 0.704674 0.703125 3.22458 0.703125 6.333C0.703125 9.44147 3.22303 11.9614 6.3315 11.9614C6.6578 11.9614 6.97753 11.9333 7.28864 11.88L19.4431 9.22336Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M16.6306 2.82797V9.83808"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function SittingAreaIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M19 9V6C19 5.46957 18.7893 4.96086 18.4142 4.58579C18.0391 4.21071 17.5304 4 17 4H7C6.46957 4 5.96086 4.21071 5.58579 4.58579C5.21071 4.96086 5 5.46957 5 6V9"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M3 16C3 16.5304 3.21071 17.0391 3.58579 17.4142C3.96086 17.7893 4.46957 18 5 18H19C19.5304 18 20.0391 17.7893 20.4142 17.4142C20.7893 17.0391 21 16.5304 21 16V11C21 10.4696 20.7893 9.96086 20.4142 9.58579C20.0391 9.21071 19.5304 9 19 9C18.4696 9 17.9609 9.21071 17.5858 9.58579C17.2107 9.96086 17 10.4696 17 11V12.5C17 12.6326 16.9473 12.7598 16.8536 12.8536C16.7598 12.9473 16.6326 13 16.5 13H7.5C7.36739 13 7.24021 12.9473 7.14645 12.8536C7.05268 12.7598 7 12.6326 7 12.5V11C7 10.4696 6.78929 9.96086 6.41421 9.58579C6.03914 9.21071 5.53043 9 5 9C4.46957 9 3.96086 9.21071 3.58579 9.58579C3.21071 9.96086 3 10.4696 3 11V16Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M5 18V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M19 18V20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function SlippersIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-2)`}>
				<mask
					id={`${id}-0`}
					style={{ maskType: "luminance" }}
					maskUnits="userSpaceOnUse"
					x="0"
					y="0"
					width="24"
					height="24"
				>
					<path d="M24 0H0V24H24V0Z" fill="white" />
				</mask>
				<g mask={`url(#${id}-0)`}>
					<mask
						id={`${id}-1`}
						style={{ maskType: "luminance" }}
						maskUnits="userSpaceOnUse"
						x="0"
						y="0"
						width="24"
						height="24"
					>
						<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
					</mask>
					<g mask={`url(#${id}-1)`}>
						<path
							d="M10.6582 11.7192H1.21631V5.25444C1.21631 2.79505 3.21004 0.801318 5.66943 0.801318H6.20512C8.66451 0.801318 10.6582 2.79505 10.6582 5.25444V11.7192Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M22.7837 11.7192H13.3417V5.25444C13.3417 2.79505 15.3355 0.801318 17.7949 0.801318H18.3305C20.7899 0.801318 22.7837 2.79505 22.7837 5.25444V11.7192Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M6.95405 23.1987H4.92047C3.34125 23.1987 2.0611 21.9185 2.0611 20.3393V11.7193H9.81342V20.3393C9.81342 21.9185 8.53322 23.1987 6.95405 23.1987Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M19.0795 23.1987H17.046C15.4667 23.1987 14.1866 21.9185 14.1866 20.3393V11.7193H21.9389V20.3393C21.9389 21.9185 20.6587 23.1987 19.0795 23.1987Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M10.6582 11.7192H1.21631V9.09946H10.6582V11.7192Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M22.7837 11.7192H13.3417V9.09946H22.7837V11.7192Z"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeMiterlimit="10"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</g>
				</g>
			</g>
			<defs>
				<clipPath id={`${id}-2`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function ToiletriesIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<g clipPath={`url(#${id}-0)`}>
				<path
					d="M13.8315 23.2964C11.0479 23.2964 5.06591 23.2964 2.28239 23.2964C0.670496 21.4091 0.670543 18.63 2.28239 16.7427C5.06595 16.7427 11.0479 16.7427 13.8315 16.7427C14.5529 17.6811 15.0504 18.8503 15.0386 20.0195C15.0504 21.1888 14.553 22.358 13.8315 23.2964Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M2.28241 16.7427V14.5518C2.28241 13.9995 2.73012 13.5518 3.28241 13.5518H12.8315C13.3838 13.5518 13.8315 13.9995 13.8315 14.5518V16.7427"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M18.0061 3.72806H15.5714C15.0191 3.72806 14.5714 4.17578 14.5714 4.72806V5.53582C14.5714 6.08811 15.0191 6.53582 15.5714 6.53582H18.0061C18.5584 6.53582 19.0061 6.08811 19.0061 5.53582V4.72806C19.0061 4.17578 18.5584 3.72806 18.0061 3.72806Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M15.0386 20.2095H22.9265"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M13.9271 23.2981H21.9265C22.4788 23.2981 22.9265 22.8504 22.9265 22.2981V12.6136C22.9265 12.0613 22.4788 11.6136 21.9265 11.6136H14.8203C14.268 11.6136 13.8203 12.0613 13.8203 12.6136V17.0473"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M22.9265 15.3902V10.0851C22.9265 9.78166 22.7887 9.49463 22.552 9.30484L19.3719 6.75557C19.1945 6.61333 18.9738 6.53582 18.7464 6.53582H14.8311C14.6037 6.53582 14.3831 6.61333 14.2056 6.75557L11.0255 9.30484C10.7887 9.49463 10.6509 9.78166 10.6509 10.0851V13.0473"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M13.9271 0.70195H21.2247C21.4638 0.70195 21.6949 0.787596 21.8763 0.943365L23.0229 1.92829"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M16.7888 3.72806V0.70195"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
			</g>
			<defs>
				<clipPath id={`${id}-0`}>
					<rect width="24" height="24" fill="white" />
				</clipPath>
			</defs>
		</Icon>
	)
}

export function TowelsIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M22 7H20"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M6.5 3H17.5C18.163 3 18.7989 3.26339 19.2678 3.73223C19.7366 4.20107 20 4.83696 20 5.5V20C20 20.2652 19.8946 20.5196 19.7071 20.7071C19.5196 20.8946 19.2652 21 19 21H10C9.73478 21 9.48043 20.8946 9.29289 20.7071C9.10536 20.5196 9 20.2652 9 20V5.5C9 4.83696 8.73661 4.20107 8.26777 3.73223C7.79893 3.26339 7.16304 3 6.5 3ZM6.5 3C5.83696 3 5.20107 3.26339 4.73223 3.73223C4.26339 4.20107 4 4.83696 4 5.5V17C4 17.2652 4.10536 17.5196 4.29289 17.7071C4.48043 17.8946 4.73478 18 5 18H9"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9 7H2"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function TvIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="0"
				width="24"
				height="24"
			>
				<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M23.2969 17.1417H0.703125V4.1575H23.2969V17.1417Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</g>
			<path
				d="M10.2192 19.8425V17.1417"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M13.7807 17.1417V19.8425"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M7.61255 19.8425H16.3873"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function WakeUpCallServiceIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M13.832 16.568C14.0385 16.6628 14.2712 16.6845 14.4917 16.6294C14.7122 16.5744 14.9073 16.4458 15.045 16.265L15.4 15.8C15.5863 15.5516 15.8279 15.35 16.1056 15.2111C16.3833 15.0723 16.6895 15 17 15H20C20.5304 15 21.0391 15.2107 21.4142 15.5858C21.7893 15.9609 22 16.4696 22 17V20C22 20.5304 21.7893 21.0391 21.4142 21.4142C21.0391 21.7893 20.5304 22 20 22C15.2261 22 10.6477 20.1036 7.27208 16.7279C3.89642 13.3523 2 8.7739 2 4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H7C7.53043 2 8.03914 2.21071 8.41421 2.58579C8.78929 2.96086 9 3.46957 9 4V7C9 7.31049 8.92771 7.61672 8.78885 7.89443C8.65 8.17214 8.44839 8.41371 8.2 8.6L7.732 8.951C7.54842 9.09118 7.41902 9.29059 7.36579 9.51535C7.31256 9.74012 7.33878 9.97638 7.44 10.184C8.80668 12.9599 11.0544 15.2048 13.832 16.568Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

export function WardrobeIcon(props: IconProps) {
	const id = useSvgId()
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<mask
				id={`${id}-0`}
				style={{ maskType: "luminance" }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="0"
				width="24"
				height="24"
			>
				<path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white" />
			</mask>
			<g mask={`url(#${id}-0)`}>
				<path
					d="M21.8894 22.2969C21.8894 22.8492 21.4417 23.2969 20.8894 23.2969H3.11047C2.55819 23.2969 2.11047 22.8492 2.11047 22.2969V1.70313C2.11047 1.15084 2.55819 0.703127 3.11047 0.703127H20.8894C21.4417 0.703127 21.8894 1.15084 21.8894 1.70313V22.2969Z"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M2.11047 17.6674H21.8894"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
				<path
					d="M12 17.6674V0.703141"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeMiterlimit="10"
				/>
			</g>
			<path
				d="M14.8147 10.8272V7.54329"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
			<path
				d="M10.358 20.4821H13.642"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
			<path
				d="M9.18518 10.8272V7.54329"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeMiterlimit="10"
			/>
		</Icon>
	)
}

export function WorkDeskIcon(props: IconProps) {
	return (
		<Icon {...props} viewBox="0 0 24 24" fill="none">
			<path
				d="M10.293 2.293C10.4806 2.10553 10.7349 2.00021 11 2.00021C11.2652 2.00021 11.5195 2.10553 11.707 2.293L14.207 4.793L20.201 6.02C20.378 6.05628 20.5418 6.1398 20.6751 6.2617C20.8084 6.3836 20.9061 6.53932 20.958 6.71233C21.0099 6.88534 21.014 7.06917 20.9698 7.2443C20.9256 7.41944 20.8348 7.57933 20.707 7.707L13.707 14.707C13.5794 14.8348 13.4195 14.9256 13.2443 14.9698C13.0692 15.014 12.8854 15.0099 12.7124 14.958C12.5394 14.9061 12.3836 14.8083 12.2617 14.675C12.1398 14.5418 12.0563 14.3779 12.02 14.201L10.793 8.207L8.29303 5.707C8.10556 5.51947 8.00024 5.26516 8.00024 5C8.00024 4.73484 8.10556 4.48053 8.29303 4.293L10.293 2.293Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M14.207 4.793L10.793 8.207"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M3 20C3 19.4696 3.21071 18.9609 3.58579 18.5858C3.96086 18.2107 4.46957 18 5 18H9C9.53043 18 10.0391 18.2107 10.4142 18.5858C10.7893 18.9609 11 19.4696 11 20V21C11 21.2652 10.8946 21.5196 10.7071 21.7071C10.5196 21.8946 10.2652 22 10 22H4C3.73478 22 3.48043 21.8946 3.29289 21.7071C3.10536 21.5196 3 21.2652 3 21V20Z"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9.08598 6.5L4.29298 11.293C4.14233 11.4435 4.04364 11.6381 4.01126 11.8485C3.97889 12.0589 4.01452 12.2742 4.11298 12.463L6.99998 18"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</Icon>
	)
}

const AmenityIcons: Record<string, (props: IconProps) => JSX.Element> = {
	// Hotel amenities
	"24_hour_security": TwentyFourHourSecurityIcon,
	"24_hour_front_desk": TwentyFourHourFrontDeskIcon,
	accessible_bathroom_grab_rails: AccessibleBathroomGrabRailsIcon,
	accessible_rooms: AccessibleRoomsIcon,
	airport_shuttle: AirportShuttleIcon,
	atm: AtmIcon,
	babysitting_childcare: BabysittingChildcareIcon,
	banquet_facilities: BanquetFacilitiesIcon,
	bar: BarIcon,
	bicycle_rental: BicycleRentalIcon,
	braille_tactile_signage: BrailleTactileSignageIcon,
	business_centre: BusinessCentreIcon,
	car_rental: CarRentalIcon,
	cctv: CctvIcon,
	childrens_play_area: ChildrensPlayAreaIcon,
	coffee_shop: CoffeeShopIcon,
	concierge: ConciergeIcon,
	conference_hall: ConferenceHallIcon,
	currency_exchange: CurrencyExchangeIcon,
	designated_smoking_area: DesignatedSmokingAreaIcon,
	dry_cleaning: DryCleaningIcon,
	elevator_lift: ElevatorLiftIcon,
	ev_charging_station: EvChargingStationIcon,
	fax_photocopy_printer: FaxPhotocopyPrinterIcon,
	fire_extinguishers: FireExtinguishersIcon,
	first_aid_kit: FirstAidKitIcon,
	fitness_centre_gym: FitnessCentreGymIcon,
	free_parking: FreeParkingIcon,
	garden: GardenIcon,
	key_card_access: KeyCardAccessIcon,
	kids_club: KidsClubIcon,
	kids_meals: KidsMealsIcon,
	kids_pool: KidsPoolIcon,
	laundry_service: LaundryServiceIcon,
	lounge: LoungeIcon,
	luggage_storage: LuggageStorageIcon,
	massage: MassageIcon,
	non_smoking_property: NonSmokingPropertyIcon,
	paid_parking: PaidParkingIcon,
	pet_friendly: PetFriendlyIcon,
	restaurant: RestaurantIcon,
	room_service: RoomServiceIcon,
	safety_deposit_box_reception: SafetyDepositBoxReceptionIcon,
	salon_beauty_services: SalonBeautyServicesIcon,
	smoke_detectors: SmokeDetectorsIcon,
	spa_and_wellness_centre: SpaAndWellnessCentreIcon,
	swimming_pool_indoor: SwimmingPoolIndoorIcon,
	swimming_pool_outdoor: SwimmingPoolOutdoorIcon,
	terrace_sun_terrace: TerraceSunTerraceIcon,
	tour_desk: TourDeskIcon,
	valet_parking: ValetParkingIcon,
	wheelchair_accessible: WheelchairAccessibleIcon,
	wifi: WifiIcon,
	yoga_classes: YogaClassesIcon,
	// Room amenities
	air_conditioning: AirConditioningIcon,
	blackout_curtains: BlackoutCurtainsIcon,
	daily_housekeeping: DailyHousekeepingIcon,
	electric_blanket: ElectricBlanketIcon,
	electric_kettle: ElectricKettleIcon,
	geyser: GeyserIcon,
	hairdryer: HairdryerIcon,
	sitting_area: SittingAreaIcon,
	slippers: SlippersIcon,
	toiletries: ToiletriesIcon,
	towels: TowelsIcon,
	tv: TvIcon,
	wake_up_call_service: WakeUpCallServiceIcon,
	wardrobe: WardrobeIcon,
	work_desk: WorkDeskIcon,
}

export default AmenityIcons

/**
 * Icon for an amenity key. Renders nothing for custom amenities (null key) or
 * keys without an icon.
 */
export function AmenityIcon({
	amenityKey,
	...props
}: { amenityKey: string | null } & IconProps) {
	const IconForKey = amenityKey ? AmenityIcons[amenityKey] : undefined
	return IconForKey ? <IconForKey {...props} /> : null
}
